import {Component, OnInit} from '@angular/core';
import {RxComponent} from '../../../../../core/components/common/rx.component';
import {SurchargeModel} from '../model/surcharge.model';
import {Observable, Subscription} from 'rxjs';
import {SurchargesService} from '../surcharges.service';
import {NotificationService} from '../../../../../core/services/notification.service';
import {ActivatedRoute, Router} from '@angular/router';

@Component({
    selector: 'itpx-surcharges-edit',
    templateUrl: './surcharges.edit.component.html',
    styleUrls: ['./surcharges.edit.component.css']
})
export class SurchargesEditComponent extends RxComponent implements OnInit {

    public initialized: boolean = false;
    public surchargeModel: SurchargeModel = <SurchargeModel>{
        rules: [],
        type: null,
        pricings: []
    };

    private ngUnSubscribe: Subscription;
    private routeSub: Subscription;
    private surchargeSchema: any = {};
    private allCurrencies: any = [];
    private allDictionaryObs = {};
    private fulfillmentTypes: any = [];

    //**********************************************
    //  Selected currency and input for surcharge
    //**********************************************
    private selectedCurrency = {
        currency: null, amount: null
    };

    //**********************************************
    //  Collection of selected currencies
    //**********************************************
    private currencyContainer = [];

    //**********************************************
    //  Selected rule and input for surcharge
    //**********************************************
    private selectedRuleDetails = {
        rule: null, operator: null, input: []
    };

    //**********************************************
    //  Collection of selected rules
    //**********************************************
    private ruleContainer = [];

    //**********************************************
    //  HELPERS
    //**********************************************
    private operatorVsSettingsMap: any = {};

    constructor(
        private surchargesService: SurchargesService,
        private route: ActivatedRoute,
        private notificationService: NotificationService,
        private router: Router
    ) {super();}

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(params => {
            const id = params['id'];
            this.fetchSurchargeDetails(id);
        });
    }

    fetchSurchargeDetails(id) {
        this.surchargesService.fetchSurchargeDetails(id).bindToLifecycle(this).subscribe((surchargeDetails) => {
            this.surchargeModel = surchargeDetails;
            this.fetchAllData();
        });
    }

    fetchAllData() {
        this.ngUnSubscribe = Observable.combineLatest(
            this.surchargesService.getAllCurrencies(),
            this.surchargesService.getSurchargeSchema(),
            this.surchargesService.getProductExamFulfillmentDict()
        ).subscribe(([currencies, surchargeSchema, fulfillmentTypes]) => {
            this.allCurrencies = currencies;
            this.surchargeSchema = surchargeSchema;
            this.fulfillmentTypes = fulfillmentTypes.data;
            this.fetchAllConfigurations();
        });
    }

    fetchAllConfigurations() {
        let observables = [];
        this.surchargeSchema.rules.map((rule) => {
            const ruleType = rule && rule.value && rule.value.type;
            if (ruleType === 'config') {
                const configType = rule && rule.value && rule.value.configType;
                observables.push(
                    this.surchargesService.getConfigListByKey(configType)
                        .map(_ => _.data)
                        .map(_ => {return {type:configType, data: this.keyValueMapper(_)}}));
            }
            if (ruleType === 'dictionary') {
                const configType = rule && rule.value && rule.value.configType;
                observables.push(
                    this.surchargesService.getDictionaryByType(configType)
                        .map(_ => {return {type: configType, data: _}}));
            }
        });
        Observable.combineLatest(observables).bindToLifecycle(this).subscribe((allData: Array<any>) => {
            allData.map(_ => {
                this.allDictionaryObs[_.type] = _.data;
            });
            this.initialize();
        });
    }

    initialize() {
        this.surchargeSchema.operators.map((op) => {this.operatorVsSettingsMap[op.key] = op;});

        this.allCurrencies = this.allCurrencies.filter((currency) => {
            let currencyExists = this.surchargeModel.pricings.filter(_ => _.currency === currency.configKey)[0];
            if (currencyExists) {
                this.currencyContainer.push({currency: currency, amount: currencyExists.amount});
                return false;
            }
            return true;
        });
        this.surchargeSchema.rules = this.surchargeSchema.rules.filter((rule) => {
            let ruleExists: any = this.surchargeModel.rules.filter(_ => _.field === rule.key)[0];
            if (ruleExists) {
                this.addSurchargeRule({input: ruleExists.value, operator: ruleExists.operator, rule: rule});
                return false;
            }
            return true;
        });
        if (this.surchargeModel.type === 'percent') {
            this.selectedCurrency.amount = this.surchargeModel.pricings[0] ? this.surchargeModel.pricings[0].amount : 0;
        }
        this.initialized = true;
    }

    onSurchargeNameChange() {
        if (!this.surchargeModel.id) {
            this.surchargeModel.key = this.surchargeModel.name ? this.surchargeModel.name.toLowerCase().trim().replace(/\s/g, '-') : '';
        }
    }

    resetSurchargeRuleInput() {
        if (this.selectedRuleDetails.rule) {
            this.selectedRuleDetails.input = [];
        }
    }

    addSurchargeCurrency() {
        this.currencyContainer.push({...this.selectedCurrency});
        this.allCurrencies = this.allCurrencies.filter(curr => curr.configKey !== this.selectedCurrency.currency.configKey);
        this.selectedCurrency = {
            currency: null, amount: null
        };
    }

    isSurchargeCurrencyValid() {
        return this.selectedCurrency.currency && this.selectedCurrency.amount;
    }

    hasInputBeDisabled() {
        const operatorConfig = this.selectedRuleDetails.rule &&
            this.selectedRuleDetails.operator &&
            this.selectedRuleDetails.input.length &&
            this.operatorVsSettingsMap[this.selectedRuleDetails.operator];

        if (operatorConfig && operatorConfig.multi) {
            return operatorConfig.max ? operatorConfig.max === this.selectedRuleDetails.input.length : false;
        }
        else {
            return this.selectedRuleDetails.input.length === 1;
        }
    }

    removeSurchargeCurrency(item) {
        this.allCurrencies.push({...item.currency});
        this.currencyContainer = this.currencyContainer.filter(_ => _.currency.id !== item.currency.id);
    }

    addSurchargeRule(selectedRuleDetails?) {
        selectedRuleDetails = selectedRuleDetails || this.selectedRuleDetails;

        this.ruleContainer.push({
            summary: {
                displayName: selectedRuleDetails.rule.displayName,
                displayOperator: this.operatorVsSettingsMap[selectedRuleDetails.operator].displayName,
                displayValue: this.getInputSummaryByOperator(selectedRuleDetails)
            },
            ruleDetails: {...selectedRuleDetails}
        });
        this.surchargeSchema.rules = this.surchargeSchema.rules.filter((rule) => {
            return rule.key !== selectedRuleDetails.rule.key;
        });
        this.selectedRuleDetails = {
            rule: null, operator: null, input: []
        };
    }

    isSurchargeAddRuleButtonValid() {
        const operatorConfig = this.selectedRuleDetails.rule &&
            this.selectedRuleDetails.operator &&
            this.selectedRuleDetails.input.length &&
            this.operatorVsSettingsMap[this.selectedRuleDetails.operator];
        if (!operatorConfig) {
            return false;
        }
        return operatorConfig && operatorConfig.multi ? (operatorConfig.max ? operatorConfig.max === this.selectedRuleDetails.input.length : true) : true;
    }

    removeSurchargeRule(item, index) {
        this.surchargeSchema.rules.push(item.ruleDetails.rule);
        this.ruleContainer.splice(index, 1);
    }

    saveSurchargeRule() {
        if (this.isSurchargeAddRuleButtonValid()) {
            this.addSurchargeRule();
        }
        this.buildSurchargePayload();
        this.surchargesService.editSurcharge(this.surchargeModel).bindToLifecycle(this).subscribe((newSurcharge) => {
            this.notificationService.success('Surcharge edited successfully.');
            this.cancelSavingSurchargeRule();
        });
    }

    isSurchargeValidToSave() {
        return this.surchargeModel.key &&
            this.surchargeModel.name &&
            this.surchargeModel.type &&
            (this.surchargeModel.type === 'fixed' ? !!this.currencyContainer.length : this.selectedCurrency.amount);
    }

    buildSurchargePayload() {
        if (this.surchargeModel.type === 'fixed') {
            this.surchargeModel.pricings = this.currencyContainer.map(_ => {return {currency: _.currency.configKey, amount: _.amount}});
        }
        else if(this.surchargeModel.type === 'percent') {
            this.surchargeModel.pricings = [{amount: this.selectedCurrency.amount}];
        }
        this.surchargeModel.scope = 'GLOBAL';
        this.surchargeModel.rules = this.ruleContainer.map((_) => {
            return {
                field: _.ruleDetails.rule.key,
                operator: _.ruleDetails.operator,
                value: _.ruleDetails.rule.key === 'fulfillment' && !Array.isArray(_.ruleDetails.input) ? [_.ruleDetails.input] :  _.ruleDetails.input
            }
        });
    }

    cancelSavingSurchargeRule() {
        this.router.navigate(['/agora/product-data/surcharges/']);
    }

    getInputSummaryByOperator(selectedRule) {
        const op = selectedRule.operator;
        const ruleType = selectedRule.rule.value.type;
        const ruleConfigType = selectedRule.rule.value.configType;
        let input: Array<string> = selectedRule.input;

        if (ruleType === 'config') {
            input = selectedRule.input.map((_) => {
                return this.allDictionaryObs[ruleConfigType][_];
            });
        }
        else if (ruleType === 'bool') {
            input = selectedRule.input.map((_) => {
                return _ ? 'Yes' : 'No';
            });
        } else if (ruleType === 'dictionary') {
            input = selectedRule.input.map((_) => {
                return this.allDictionaryObs[ruleConfigType][_];
            });
            return input;
        }

        const config = {
            'range' : input.join(' to '),
        };
        if (config.hasOwnProperty(op)) {
            return config[op];
        }
        return input.join(', ') || '';
    }

    getNumberOfInputs() {
        if (!this.selectedRuleDetails.rule || !this.selectedRuleDetails.operator) {
            return [];
        }
        const type = this.selectedRuleDetails && this.selectedRuleDetails.rule && this.selectedRuleDetails.rule.value.type;
        let arr = [];
        let config = this.operatorVsSettingsMap[this.selectedRuleDetails.operator];
        arr.length = config.multi && type !== 'bool' ? (config.max || 2) : 1;
        return arr;
    }

    isInputConfigType() {
        return this.selectedRuleDetails &&
            this.selectedRuleDetails.rule &&
            this.selectedRuleDetails.rule.value.type;
    }

    keyValueMapper(data: Array<any>) {
        let result = {};
        data.map((_) => {
            result[_.configKey] = _.configValue
        });
        return result;
    }

    getKeys(obj) {
        return Object.keys(obj);
    }

    ngOnDestroy() {
        this.ngUnSubscribe.unsubscribe();
    }
}



// WEBPACK FOOTER //
// ./src/app/agora/components/product-data/surcharges/edit/surcharges.edit.component.ts